import { Box, Button, Flex, IconButton } from '@chakra-ui/react'
import type { IconDefinition } from '@fortawesome/fontawesome-common-types'
import { faTimes } from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import React, { useCallback, useEffect, useState } from 'react'

import { noOp } from '@/utils/helpers'

import { InputText } from './UI/Form/styled'
import Icon from './UI/Icon/Icon'

const TextInput = React.forwardRef<
  HTMLInputElement,
  {
    initialValue?: string
    placeholder?: string
    size?: number
    buttonText?: string
    buttonVariant?: string
    hideButton?: boolean
    disableSubmitOnEnter?: boolean
    resetOnSubmit?: boolean
    icon?: IconDefinition | null
    disableButtonWithoutInput?: boolean
    onSubmit?: (input: string) => void | Promise<void>
    onClear?: (() => void) | null
    disabled?: boolean
  }
>(
  (
    {
      initialValue = '',
      placeholder = '',
      size = 20,
      buttonText = 'Submit',
      buttonVariant = 'solid',
      hideButton = false,
      onSubmit = noOp,
      onClear = null,
      disableSubmitOnEnter = false,
      resetOnSubmit = false,
      icon = null,
      disableButtonWithoutInput = false,
      disabled = false,
    },
    ref
  ) => {
    const [input, setInput] = useState(initialValue)

    // Reset input when consumer changes initialValue
    useEffect(() => {
      setInput(initialValue)
    }, [initialValue])

    const onSubmitCallback = useCallback(
      (text: string) => {
        Promise.resolve(onSubmit(text))
          .then(() => {
            if (resetOnSubmit) {
              setInput('')
            }
          })
          .catch(_err => {
            // No-op
          })
      },
      [onSubmit, resetOnSubmit]
    )

    const onClearCallback = useCallback(() => {
      setInput('')
      if (onClear) {
        onClear()
      }
    }, [onClear])

    return (
      <Flex flexDir="row" position="relative">
        <Flex flexDir="row" position="relative" width="100%">
          {icon ? (
            <Box position="absolute" pl="12px" pt="12px">
              <Icon faIcon={icon} size="sm" color="fg.subtle" />{' '}
            </Box>
          ) : null}

          <InputText
            ref={ref}
            type="text"
            size={size}
            style={{ paddingLeft: icon ? '40px' : '14px' }}
            placeholder={placeholder}
            value={input}
            onInput={e => setInput((e.target as HTMLInputElement).value)}
            onKeyPress={e => {
              if (!disableSubmitOnEnter && e.key === 'Enter') {
                onSubmitCallback(input)
              }
            }}
            disabled={disabled}
          />
          {onClear !== null && input !== '' ? (
            <IconButton
              aria-label="Clear search"
              variant="ghost"
              color="fg.subtle"
              position="absolute"
              right="5px"
              icon={<FontAwesomeIcon icon={faTimes} />}
              onClick={onClearCallback}
            />
          ) : null}
        </Flex>

        {hideButton ? null : (
          <Button
            width="100px"
            variant={buttonVariant}
            onClick={() => onSubmitCallback(input)}
            ml={2}
            isDisabled={disableButtonWithoutInput === true && input === ''}
            _hover={{
              opacity: 0.6,
              cursor: 'pointer',
            }}
            _disabled={{
              opacity: 0.5,
            }}
          >
            {buttonText}
          </Button>
        )}
      </Flex>
    )
  }
)

export default TextInput
